import * as THREE from 'three';
import type { CelestialBody } from '../scene/CelestialBody';

export class FlyControls {
  camera: THREE.PerspectiveCamera;
  domElement: HTMLElement;

  /** World velocity (scene units / s) */
  velocity = new THREE.Vector3();
  /** Look euler (yaw, pitch) */
  yaw = 0;
  pitch = 0;

  /** Base speed multiplier (scroll-controlled, exponential) */
  speedTier = 8; // log2-ish index
  readonly minTier = 0;
  readonly maxTier = 22;

  acceleration = 1;
  damping = 0.92;
  lookSensitivity = 0.0022;
  boost = false;

  enabled = true;
  pointerLocked = false;

  private keys = new Set<string>();
  private euler = new THREE.Euler(0, 0, 0, 'YXZ');
  private direction = new THREE.Vector3();
  private right = new THREE.Vector3();
  private up = new THREE.Vector3(0, 1, 0);
  private wish = new THREE.Vector3();
  private tmp = new THREE.Vector3();

  private bodies: CelestialBody[] = [];
  private onLockChange?: () => void;

  constructor(camera: THREE.PerspectiveCamera, domElement: HTMLElement) {
    this.camera = camera;
    this.domElement = domElement;

    // Start looking at solar system center from near Earth orbit region
    this.yaw = Math.PI * 0.15;
    this.pitch = -0.12;
    this.applyLook();

    this.bind();
  }

  setBodies(bodies: CelestialBody[]): void {
    this.bodies = bodies;
  }

  get speed(): number {
    // Exponential tiers from near-surface crawl to outer-system cruise
    return Math.pow(2, this.speedTier * 0.55) * 0.35;
  }

  get speedMagnitude(): number {
    return this.velocity.length();
  }

  private bind(): void {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    this.domElement.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    this.domElement.addEventListener('wheel', this.onWheel, { passive: false });
    // prevent context menu on right-drag style use
    this.domElement.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.domElement.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    this.domElement.removeEventListener('wheel', this.onWheel);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (this.isTypingTarget(e.target)) return;
    this.keys.add(e.code);
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') this.boost = true;
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') this.boost = false;
  };

  private isTypingTarget(t: EventTarget | null): boolean {
    if (!(t instanceof HTMLElement)) return false;
    const tag = t.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || t.isContentEditable;
  }

  private onMouseDown = (): void => {
    if (!this.enabled) return;
    if (document.pointerLockElement !== this.domElement) {
      this.domElement.requestPointerLock();
    }
  };

  private onPointerLockChange = (): void => {
    this.pointerLocked = document.pointerLockElement === this.domElement;
    this.onLockChange?.();
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.enabled || !this.pointerLocked) return;
    this.yaw -= e.movementX * this.lookSensitivity;
    this.pitch -= e.movementY * this.lookSensitivity;
    const lim = Math.PI / 2 - 0.02;
    this.pitch = Math.max(-lim, Math.min(lim, this.pitch));
    this.applyLook();
  };

  private onWheel = (e: WheelEvent): void => {
    if (!this.enabled) return;
    // Don't steal wheel when focused on search
    if (this.isTypingTarget(document.activeElement)) return;
    e.preventDefault();
    const delta = Math.sign(e.deltaY);
    this.speedTier = Math.max(
      this.minTier,
      Math.min(this.maxTier, this.speedTier + delta * 0.45),
    );
  };

  applyLook(): void {
    this.euler.set(this.pitch, this.yaw, 0, 'YXZ');
    this.camera.quaternion.setFromEuler(this.euler);
  }

  setLookFromQuaternion(q: THREE.Quaternion): void {
    this.euler.setFromQuaternion(q, 'YXZ');
    this.pitch = this.euler.x;
    this.yaw = this.euler.y;
    this.applyLook();
  }

  /** Soft stop thrusters (e.g. during teleport) */
  dampVelocity(factor = 0.85): void {
    this.velocity.multiplyScalar(factor);
  }

  update(dt: number): void {
    if (!this.enabled) {
      this.velocity.multiplyScalar(Math.pow(this.damping, dt * 60));
      this.camera.position.addScaledVector(this.velocity, dt);
      this.resolveCollisions();
      return;
    }

    // Movement intent in camera space
    this.wish.set(0, 0, 0);
    if (this.keys.has('KeyW')) this.wish.z -= 1;
    if (this.keys.has('KeyS')) this.wish.z += 1;
    if (this.keys.has('KeyA')) this.wish.x -= 1;
    if (this.keys.has('KeyD')) this.wish.x += 1;
    if (this.keys.has('KeyQ')) this.wish.y -= 1;
    if (this.keys.has('KeyE')) this.wish.y += 1;
    // Space / Ctrl as alternate up/down
    if (this.keys.has('Space')) this.wish.y += 1;
    if (this.keys.has('ControlLeft') || this.keys.has('ControlRight')) this.wish.y -= 1;

    if (this.wish.lengthSq() > 0) {
      this.wish.normalize();
      this.direction.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
      this.right.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
      // Use world up for strafe-vertical when not fully rolled (we don't roll)
      this.tmp.set(0, 0, 0);
      this.tmp.addScaledVector(this.right, this.wish.x);
      this.tmp.addScaledVector(this.up, this.wish.y);
      this.tmp.addScaledVector(this.direction, -this.wish.z);
      if (this.tmp.lengthSq() > 0) this.tmp.normalize();

      const accel = this.speed * (this.boost ? 2.8 : 1) * 2.2;
      this.velocity.addScaledVector(this.tmp, accel * dt);
    }

    // Cap max speed relative to tier
    const maxSpd = this.speed * (this.boost ? 3.2 : 1.35);
    const spd = this.velocity.length();
    if (spd > maxSpd) this.velocity.multiplyScalar(maxSpd / spd);

    // Inertia damping
    const damp = Math.pow(this.damping, dt * 60);
    if (this.wish.lengthSq() === 0) {
      this.velocity.multiplyScalar(damp);
    } else {
      this.velocity.multiplyScalar(Math.pow(0.97, dt * 60));
    }

    this.camera.position.addScaledVector(this.velocity, dt);
    this.resolveCollisions();
  }

  private resolveCollisions(): void {
    const pos = this.camera.position;
    for (const body of this.bodies) {
      const center = body.worldPosition;
      const minDist = body.collisionRadius * 1.08;
      this.tmp.copy(pos).sub(center);
      const d = this.tmp.length();
      if (d < minDist && d > 1e-8) {
        this.tmp.multiplyScalar(minDist / d);
        pos.copy(center).add(this.tmp);
        // kill inward velocity
        const inward = this.velocity.dot(this.tmp.clone().normalize());
        if (inward < 0) {
          this.velocity.addScaledVector(this.tmp.normalize(), -inward);
        }
      } else if (d < 1e-8) {
        pos.copy(center).add(new THREE.Vector3(minDist, 0, 0));
      }
    }
  }
}
